<template>
    <div class="billing-settings">
        <div class="row mb-3 mb-md-4 mb-lg-5">
            <div class="row align-items-stretch m-0 px-0">
                <div class="col-lg-4 col-md-6 col-12 mb-3">
                    <div class="card h-100">
                        <div class="card-body">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <h4 class="mb-0">Payment Method</h4>
                                <div>
                                    <button v-if="entityType === 'agency' || canEdit('billing')" class="btn btn-secondary btn-icon-only-sm"
                                    data-bs-toggle="tooltip" title="Update Payment Method" @click="startUpdatePaymentMethod">
                                        <img class="icon" src="/img/icons/avid-icons/pencil.svg" alt="">
                                    </button>
                                    <div v-else data-bs-toggle="tooltip" title="You do not have privileges to change the payment method">
                                        <button class="btn btn-secondary btn-icon-only-sm" :disabled="true" aria-label="Button">
                                            <img class="icon" src="/img/icons/avid-icons/pencil.svg" alt="">
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div v-if="entity.payment_method === 'stripe'">
                                <div v-if="payment_loading" class="d-flex justify-content-center align-items-center" style="height:120px">
                                    <div class="spinner-border text-primary" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                                <template v-else>
                                    <div v-if="payment_method" class="payment-card-container mb-4">
                                        <div class="payment-card" :style="cardStyle">
                                            <div class="payment-card-content">
                                                <div class="payment-card-header">
                                                    <span class="payment-card-label">
                                                        {{ payment_method.type === 'bank' ? (payment_method.bank_name ? propercase(payment_method.bank_name) : 'Bank Account') : 'Primary Card' }}
                                                    </span>
                                                </div>
                                                <div class="payment-card-number">
                                                    {{ maskedNumber }} {{ payment_method.last4 }}
                                                </div>
                                                <div class="payment-card-footer">
                                                    <div class="payment-card-expiry" v-if="payment_method.exp_month">
                                                        {{ payment_method.exp_month.toString().padStart(2,'0') }}/{{ payment_method.exp_year.toString().slice(-2) }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="payment-card-status mt-2">
                                            <span class="text-muted">Status: </span>
                                            <span class="badge badge-sm" :class="statusClass(payment_method.status)">{{ propercase(payment_method.status) }}</span>
                                        </div>
                                    </div>
                                    <p v-else class="text-muted">No payment method on file.</p>
                                </template>
                            </div>
                            <div v-else-if="entity.payment_method === 'bill.com'">
                                <p class="payment-method-display d-flex align-items-center mb-3">
                                    <span class="d-flex align-items-center justify-content-center me-3">
                                        <img src="/img/icons/bill_com.png" class="icon" alt="brand" style="width: 50px;" />
                                    </span>
                                    <span>
                                        <strong class="payment-type">Bill.com <br></strong>
                                        <span class="text-muted">You are currently using Bill.com for payments. <a :href="lockstepSupportLink">Please contact your LockStep Expert to make updates to your account.</a></span>
                                    </span>
                                </p>
                            </div>
                            <div v-else-if="entity.payment_method === 'ach'">
                                <p class="payment-method-display d-flex align-items-center mb-3">
                                    <span class="icon-circle d-flex align-items-center justify-content-center me-3">
                                        <img src="/img/icons/bank.svg" class="icon m-2" alt="brand" />
                                    </span>
                                    <span>
                                        <strong class="payment-type">ACH Push Payment <br></strong>
                                        <span class="text-muted">You are currently using ACH Push Payment for payments. <a :href="lockstepSupportLink">Please contact your LockStep Expert to make updates to your account.</a></span>
                                    </span>
                                </p>
                            </div>
                            <div v-else>
                                <p class="payment-method-display d-flex align-items-center mb-3">
                                    <span>
                                        <strong class="payment-type">Custom Payment Method<br></strong>
                                        <span class="text-muted">You are currently set up with a custom payment method. <a :href="lockstepSupportLink">Please contact your LockStep Expert to make updates to your account.</a></span>
                                    </span>
                                </p>
                            </div>

                            <p class="mb-1"><strong>Upcoming Payments:</strong></p>
                            <div v-if="upcoming_payments.length > 0" class="upcoming-payments-list">
                                <div v-for="payment in upcoming_payments" :key="payment.id" class="row align-items-center">
                                    <div class="col-auto" v-if="getPaymentIcon(payment) !== 'custom'">
                                        <div class="payment-icon-container">
                                            <img v-if="getPaymentIcon(payment) !== 'custom'" :src="getPaymentIcon(payment)" class="payment-icon" alt="payment method" style="width: 30px; height: 30px; object-fit: contain;" />
                                            <span v-else class="payment-text">Custom</span>
                                        </div>
                                    </div>
                                    <div class="col text-start">
                                        <span>{{ formatFullDate(payment.date) }}</span>
                                    </div>
                                    <div class="col-auto text-end">
                                        <a href="#" @click.prevent="showInvoice(payment)" class="text-decoration-none">{{ currency_with_zero(payment.net_amount) }}</a>
                                    </div>
                                </div>
                            </div>
                            <p v-else class="text-muted">No upcoming payments.</p>

                            <small v-if="agencyName != ''"><em><strong>Warning:</strong> If you are wanting to update {{agencyName}}'s payment method, <a :href="'/admin/agencies/'+ user.agency_id+ '/billing'" v-if="user.user_type === 'agency_admin'">you can do that here.</a><span v-else>contact your admin.</span>)</em></small>
                        </div>
                    </div>
                </div>

                <div class="col-lg-4 col-md-6 col-12 mb-3">
                    <div class="card h-100">
                        <div class="card-body">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <h4 class="mb-0">Billing Contact</h4>
                                <div>
                                    <button v-if="entityType === 'agency' || canEdit('billing')" class="btn btn-secondary btn-icon-only-sm" @click="startEditBillingContact"
                                    data-bs-toggle="tooltip" :title="billing_contact_form.first_name != '' ? 'Edit Billing Contact' : 'Add Billing Contact'">
                                        <img v-if="billing_contact_form.first_name != ''" class="icon" src="/img/icons/avid-icons/pencil.svg" alt=""/>
                                        <img v-else class="icon" src="/img/icons/avid-icons/plus.svg" alt=""/>
                                    </button>
                                    <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-secondary btn-icon-only-sm" :disabled="true" aria-label="Button">
                                            <img class="icon" src="/img/icons/avid-icons/pencil.svg" alt="">
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <span v-if="billing_contact_form.first_name != ''">

                                <div class="profile-header">
                                    <div class="profile-info">
                                        <h3 class="mb-0">{{ billing_contact_form.first_name }} {{ billing_contact_form.last_name }}</h3>
                                        <p>{{ billing_contact_form.email }}</p>
                                    </div>
                                </div>
                                <p class="mb-1"><span class="text-muted">Address:</span></p>
                                <p class="mb-0">{{ billing_contact_form.address }},</p>
                                <p>{{ billing_contact_form.city }}, {{ billing_contact_form.region }} {{ billing_contact_form.postal_code }}</p>
                            </span>
                            <span v-else>
                                <p class="text-muted">No billing contact found.</p>
                            </span>
                        </div>
                    </div>
                </div>

                <div class="col-lg-4 col-md-6 col-12 mb-3">
                    <div class="card h-100">
                        <div class="card-body contract-card">
                            <h4 class="mb-3">Your Agreement</h4>
                            <div class="text-center my-3">
                                <h3 class="fw-bold mb-2">{{ agreementName }}</h3>
                                <span class="text-muted">Renewal Date:</span> <span v-html="renewalDate"></span>
                            </div>
                            <hr class="contract-divider">
                            <small><p class="mb-0 text-muted">Accurate and easy reporting on the most important metrics related to your marketing and fundraising efforts. Avid's Insights will helps you easily identify the greatest opportunities and be able to take action to maximize your results.</p></small>
                        </div>
                    </div>
                </div>
                
                <div class="col-lg-4 col-md-6 col-12 mb-3" v-if="previousYearsInvoices.length > 0" >
                    <div class="card h-100">
                        <div class="card-body">
                            <h4 class="mb-0">Previous Years</h4>
                            <div class="previous-years-accordion">
                                <div class="accordion" id="previousYearsAccordion">
                                    <div v-for="(yearData, index) in previousYearsInvoices" 
                                         :key="yearData.year" 
                                         class="accordion-item border-0">
                                        <h2 class="accordion-header" :id="`heading${yearData.year}`">
                                            <button class="accordion-button collapsed previous-year-button px-0" 
                                                    type="button" 
                                                    data-bs-toggle="collapse" 
                                                    :data-bs-target="`#collapse${yearData.year}`"
                                                    :aria-expanded="false" 
                                                    :aria-controls="`collapse${yearData.year}`">
                                                <div class="d-flex align-items-center w-100">
                                                    <div class="previous-year-icon me-2 d-flex align-items-center justify-content-center">
                                                        <img src="/img/icons/avid-icons/invoice.svg" alt="Invoice" class="icon">
                                                    </div>
                                                    <span class="flex-grow-1">Invoices in {{ yearData.year }}</span>
                                                    <span class="badge bg-secondary ms-2">{{ yearData.invoices.length }}</span>
                                                    <img src="/img/icons/avid-icons/chevron-right.svg" alt="" class="accordion-chevron ms-2" style="width: 16px; height: 16px; flex-shrink: 0;">
                                                </div>
                                            </button>
                                        </h2>
                                        <div :id="`collapse${yearData.year}`" 
                                             class="accordion-collapse collapse" 
                                             :aria-labelledby="`heading${yearData.year}`"
                                             data-bs-parent="#previousYearsAccordion">
                                            <div class="accordion-body p-0">
                                                <div class="previous-year-invoices-list">
                                                    <div v-for="(invoice, invoiceIndex) in yearData.invoices" :key="invoice.id" 
                                                         :class="['previous-year-invoice-item', 'p-3', { 'border-bottom': invoiceIndex < yearData.invoices.length - 1 }]">
                                                        <div class="d-flex justify-content-between align-items-start mb-2">
                                                            <a href="#" @click.prevent="showInvoice(invoice)" class="fw-bold">{{ invoice.display_invoice_id }}</a>
                                                            <span class="text-muted small text-end">{{ utc_date(invoice.date) }}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div :class="['col-12', previousYearsInvoices.length > 0 ? 'col-lg-8' : '']">
                    <div class="card table-card">
                        <div class="card-body px-0">
                            <div class="row">
                                <div class="col-12">
                                    <h4 class="mx-3 mt-2 mb-3">
                                        Billing History
                                        <span v-if="filtered_past_invoices.length > 0"> ({{ filtered_past_invoices.length }})</span>
                                    </h4>
                                </div>
                            </div>
                            <div class="table-width-responsive">
                                <table class="table table-with-pagination mb-0">
                                    <thead>
                                        <tr class="table-header-row">
                                            <th @click.prevent="sortByKey('date')" class="d-table-cell">
                                                <div class="d-flex flex-row">
                                                    Date
                                                    <div class="d-flex flex-column">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'date' && sort_order === 'asc'}" alt="">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'date' && sort_order === 'des'}" alt="">
                                                    </div>
                                                </div>
                                            </th>
                                            <th @click.prevent="sortByKey('display_invoice_id')" class="d-table-cell">
                                                <div class="d-flex flex-row">
                                                    Invoice #
                                                    <div class="d-flex flex-column">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'display_invoice_id' && sort_order === 'asc'}" alt="">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'display_invoice_id' && sort_order === 'des'}" alt="">
                                                    </div>
                                                </div>
                                            </th>
                                            <th @click.prevent="sortByKey('display_invoice_id')" class="d-table-cell" v-if="entityType === 'agency'">
                                                <div class="d-flex flex-row">
                                                    Nonprofit Name
                                                    <div class="d-flex flex-column">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'nonprofit_name' && sort_order === 'asc'}" alt="">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'nonprofit_name' && sort_order === 'des'}" alt="">
                                                    </div>
                                                </div>
                                            </th>
                                            <th @click.prevent="sortByKey('net_amount')" class="d-table-cell">
                                                <div class="d-flex flex-row">
                                                    Amount
                                                    <div class="d-flex flex-column">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'net_amount' && sort_order === 'asc'}" alt="">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'net_amount' && sort_order === 'des'}" alt="">
                                                    </div>
                                                </div>
                                            </th>
                                            <th @click.prevent="sortByKey('status')" class="d-table-cell">
                                                <div class="d-flex flex-row">
                                                    Status
                                                    <div class="d-flex flex-column">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'status' && sort_order === 'asc'}" alt="">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'status' && sort_order === 'des'}" alt="">
                                                    </div>
                                                </div>
                                            </th>
                                            <th @click.prevent="sortByKey('paid_by')" class="d-table-cell">
                                                <div class="d-flex flex-row">
                                                    Paid By
                                                    <div class="d-flex flex-column ms-1">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'paid_by' && sort_order === 'asc'}" alt="">
                                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'paid_by' && sort_order === 'des'}" alt="">
                                                    </div>
                                                </div>
                                            </th>
                                            <th class="d-table-cell column-sort-disabled pe-3" width="1">
                                                <div class="d-flex flex-row">
                                                    Actions
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-for="(invoice, index) in list_shown" :key="invoice.id">
                                            <td class="d-table-cell">
                                                {{ utc_date(invoice.date) }}
                                            </td>
                                            <td class="d-table-cell">
                                                {{ invoice.display_invoice_id }}
                                            </td>
                                            <td class="d-table-cell" v-if="entityType === 'agency'">
                                                <span v-if="invoice.client">{{ invoice.client.name }}</span>
                                                <span v-else>N/A</span>
                                            </td>
                                            <td class="d-table-cell">
                                                {{currency(invoice.net_amount) }}
                                            </td>
                                            <td class="d-table-cell">
                                                <span class="badge" :class="getInvoiceBadgeClass(invoice.status)">
                                                    {{ getInvoiceStatusLabel(invoice.status) }}
                                                </span>
                                            </td>
                                            <td class="d-table-cell">
                                                <div v-if="invoice.paid_by && invoice.paid_by.image" data-bs-toggle="tooltip" :title="invoice.paid_by.name">
                                                    <img class="avatar rounded-circle d-inline-block" :src="invoice.paid_by.image" style="width: 30px; height: 30px;" />
                                                </div>
                                                <span v-else>{{ invoice.paid_by && invoice.paid_by.name ? invoice.paid_by.name : (invoice.paid_by ? invoice.paid_by : 'N/A') }}</span>
                                            </td>
                                        
                                            <td class="table-column-action d-none d-sm-table-cell">
                                                <div class="dropdown d-flex justify-content-center">
                                                    <button class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" data-bs-auto-close="true" aria-expanded="false" aria-label="Button">
                                                        <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" data-bs-toggle="tooltip" title="Actions" alt="" />
                                                    </button>
                                                    <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                                        <a class="dropdown-item" href="#" @click.prevent="showInvoice(invoice)">
                                                            <img class="icon me-2" src="/img/icons/avid-icons/eye.svg" alt="">View
                                                        </a>
                                                        <a class="dropdown-item" href="#" @click.prevent="downloadInvoice(invoice)" v-if="invoice.stripe_invoice_id">
                                                            <img class="icon me-2" src="/img/icons/avid-icons/download.svg" alt="">Download
                                                        </a>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tfoot class="table-border-bottom-0">
                                        <tr>
                                            <td colspan="100%" class="py-0">
                                                <div class="table-pagination">
                                                    <div class="table-pagination--group d-none d-md-flex">
                                                        <div class="position-relative">
                                                            <select class="form-select" name="num_per_page" id="num_per_page" v-model="num_per_page" @change="reloadPagination(filtered_past_invoices)" aria-label="Num Per Page">
                                                                <option value="5000">ALL</option>
                                                                <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                                            </select>
                                                            <div class="select-display-text">
                                                                Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div class="table-pagination--group d-none d-lg-flex">
                                                        {{ filtered_past_invoices.length === 0 ? '0' : item_interval }} of {{ filtered_past_invoices.length }}
                                                    </div>

                                                    <div class="table-pagination--group">
                                                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click.prevent="moveToPrevPage(filtered_past_invoices)" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                                                            <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                                                        </button>

                                                        <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                                                            <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="{'current-page': page === page_index + 1}" @click.prevent="moveToPage(page, filtered_past_invoices)">
                                                                {{page}}
                                                            </button>
                                                        </div>
                                                        <div class="d-flex page-selector" v-if="page_index_array.length > 3">
                                                            <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="{'current-page': page === page_index + 1}" @click.prevent="moveToPage(page, filtered_past_invoices)">
                                                                {{page}}
                                                            </button>

                                                            <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                                                ...
                                                            </button>
                                                            <button v-if="page_index >= 2 && page_index < page_index_array.length - 1" class="btn btn-none btn-icon-only-md current-page">
                                                                {{page_index + 1}}
                                                            </button>

                                                            <button v-if="page_index < page_index_array.length - 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled aria-hidden="true">
                                                                ...
                                                            </button>
                                                            <button :class="{'current-page': page_index === page_index_array.length - 1}" class="btn btn-none btn-icon-only-md" @click.prevent="moveToPage(page_index_array.length, filtered_past_invoices)" :key="page_index_array.length">
                                                                {{ page_index_array.length }}
                                                            </button>
                                                        </div>

                                                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click.prevent="moveToNextPage(filtered_past_invoices)" :disabled="page_index === page_count - 1" data-bs-toggle="tooltip" title="Next page">
                                                            <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>                
            </div>
        </div>
    </div>
    
    <!-- Invoice Details Modal -->
    <div class="modal fade" id="invoice-details-modal" data-bs-backdrop="static" aria-labelledby="invoice-details-modal-label" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" style="max-width: 600px;">
            <div class="modal-content invoice-details-modal">
                <div class="modal-header border-bottom-0 pb-0">
                    <div class="d-flex align-items-start w-100">
                        <div class="flex-grow-1" style="min-width: 0; margin-right: 1rem;">
                            <div class="text-center my-4">
                                <div class="text-muted mb-2" v-if="selectedInvoice">{{ formatInvoiceDate(selectedInvoice.date) }}</div>
                                <h3 class="mt-2 mb-2 fw-bold" v-if="selectedInvoice">{{ getInvoiceTitle() }}</h3>
                                <div v-if="selectedInvoice && selectedInvoice.display_invoice_id" class="text-muted small">
                                    {{ selectedInvoice.display_invoice_id }}
                                </div>
                            </div>
                        </div>
                        <div class="d-flex align-items-center" style="flex: 0 0 auto;">
                            <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close" @click="closeInvoiceModal">
                                <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt="">
                            </button>
                        </div>
                    </div>
                </div>
                <div class="modal-body" v-if="selectedInvoice">
                    <div class="invoice-line-items">
                        <div v-for="(item, index) in getInvoiceLineItems()" :key="index" class="invoice-line-item">
                            <div v-if="index > 0" class="invoice-divider"></div>
                            <div class="d-flex justify-content-between align-items-start mb-2">
                                <div class="flex-grow-1">
                                    <div class="fw-bold d-flex align-items-center gap-2">
                                        <span>{{ item.name }}</span>
                                        <span v-if="item.quantity && item.quantity > 1" class="badge bg-secondary">{{ item.quantity }}</span>
                                    </div>
                                    <div v-if="item.description" class="text-muted small mt-1">
                                        {{ item.description }}
                                    </div>
                                    <div v-if="item.taxAmount > 0" class="ms-3 mt-1 text-muted small">
                                        Sales Tax
                                    </div>
                                </div>
                                <div class="text-end">
                                    <div v-if="item.hasDiscount" class="mb-1">
                                        <div class="text-decoration-line-through text-muted">{{ currency_with_zero(item.originalAmount) }}</div>
                                        <div class="fw-bold">{{ currency_with_zero(item.amount) }}</div>
                                        <span class="badge bg-success mt-1">{{ item.discountPercent }}% off</span>
                                    </div>
                                    <div v-else class="fw-bold">{{ currency_with_zero(item.amount) }}</div>
                                    <div v-if="item.taxAmount > 0" class="mt-1 text-muted small">
                                        {{ currency_with_zero(item.taxAmount) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer pt-1" v-if="selectedInvoice">
                    <div class="d-flex justify-content-between align-items-end w-100">
                        <div>
                            <div class="text-muted small mb-1">{{ getInvoiceMonthTotal() }} Total</div>
                            <div class="fw-bold fs-4">{{ currency_with_zero(selectedInvoice.net_amount || 0) }}</div>
                        </div>
                        <button v-if="selectedInvoice.stripe_invoice_id && selectedInvoice.status !== 'draft'" 
                                @click="downloadInvoice(selectedInvoice)" 
                                class="btn btn-secondary d-flex align-items-center">
                            <img class="icon me-2" src="/img/icons/avid-icons/download.svg" style="width: 16px; height: 16px;" alt="">
                            Download Invoice
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { defineComponent } from 'vue';
    import Swal from 'sweetalert2';
    import { useNotifications } from '../../composables/useNotifications';
    import tablePaginationMixin from '../../mixins/table_pagination';
    import permissionsMixin from '../../mixins/permissions';
    import axios from 'axios';
    import { loadStripe } from '@stripe/stripe-js';
    // Country & Subdivision data for address validation    
    import { allCountries } from 'country-region-data';

    export default defineComponent({
        name: 'BillingSettings',
        components: {Swal},
        mixins: [tablePaginationMixin, permissionsMixin],
        setup() {
            const { showNotification } = useNotifications();
            return {
                showNotification
            };
        },
        props: {
            entity: {
                type: Object,
                required: true
            },
            entityType: {
                type: String,
                default: 'client', // or 'agency'
                validator: v => ['client','agency'].includes(v)
            },
            user: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                upcoming_payments: [],
                past_invoices: [],
                payment_method: null,
                payment_loading: true,
                filtered_past_invoices: [],
                list_shown: [],
                sort_key: 'date',
                sort_order: 'asc',
                num_per_page: 5, // Default to 5 items per page for Past Invoices
                selectedInvoice: null, // Currently selected invoice for modal display
                // Billing contact form data
                billing_contact_form: {
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    address: '',
                    city: '',
                    region: '',
                    postal_code: '',
                    country: ''
                }
            };
        },
        beforeMount() {
            // Only include invoices with invoice_date <= today (start of day UTC)
            const today = new Date();
            today.setUTCHours(0, 0, 0, 0);
            if(this.entity && this.entity.invoices) {
                this.fetchPaymentMethod();
                this.past_invoices = this.entity.invoices.filter(inv => {
                    if (!inv.date) return false;
                    // Parse invoice_date as date (assume YYYY-MM-DD or ISO format)
                    const invDate = new Date(inv.date);
                    invDate.setUTCHours(0, 0, 0, 0);
                    // Include if invDate is valid and either:
                    // 1. Before today, OR
                    // 2. Today but status is not 'draft'
                    const isBeforeToday = invDate < today;
                    const isTodayNotDraft = invDate.getTime() === today.getTime() && inv.status !== 'draft';
                    return !isNaN(invDate) && (isBeforeToday || isTodayNotDraft);
                });
                
                // Initially show only current year invoices
                const currentYear = new Date().getFullYear();
                this.filtered_past_invoices = this.past_invoices.filter(inv => {
                    if (!inv.date) return false;
                    const invDate = this.parseDateUTC(inv.date);
                    if (!invDate) return false;
                    return invDate.getUTCFullYear() === currentYear;
                });
            
                this.sortByKey('date'); //Sort by the date (and it will flip it to descending)
                this.reloadPagination(this.filtered_past_invoices);
                //Upcoming payments are invoices where the date is today (draft status) or in the future
                this.upcoming_payments = this.entity.invoices.filter(inv => {
                    if (!inv.date) return false;
                    const invDate = new Date(inv.date);
                    invDate.setUTCHours(0, 0, 0, 0);
                    // Include if invDate is valid and either:
                    // 1. After today, OR
                    // 2. Today and status is 'draft'
                    const isAfterToday = invDate > today;
                    const isTodayDraft = invDate.getTime() === today.getTime() && inv.status === 'draft';
                    return !isNaN(invDate) && (isAfterToday || isTodayDraft);
                }).sort((a, b) => {
                    // Sort by date in ascending order (earliest first)
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    return dateA - dateB;
                });
            }
        },
        mounted() {
            // Populate form with existing data if available
            if (this.entity && this.entity.billing_contact) {
                this.setBillingContact(this.entity.billing_contact);
            }
        },
        updated() {
            // this.$nextTick(() => {
            //     this.initializeTooltips();
            // });
        },
        computed: {
            agencyName() {
                if(this.user.agency && this.entityType !== 'agency') {
                    return this.user.agency.name;
                }
                return '';
            },
            renewalDate() {
                if (!this.entity?.subscriptions || this.entity.subscriptions.length === 0) {
                    return '<em>No active subscriptions</em>';
                }
                
                // Get all end dates from line items that are "Enterprise Subscription" or contain "Avid"
                const endDates = [];
                
                for (const subscription of this.entity.subscriptions) {
                    if (!subscription || typeof subscription !== 'object') continue;
                    const lineItems = subscription.line_items || subscription.lineItems || [];
                    for (const lineItem of lineItems) {
                        if (!lineItem || typeof lineItem !== 'object') continue;
                        const itemName = (lineItem.name != null) ? String(lineItem.name) : '';
                        if (itemName === 'Enterprise Subscription' || itemName.includes('Avid')) {
                            if (lineItem.end_date) {
                                const endDateUTC = this.parseDateUTC(lineItem.end_date);
                                if (endDateUTC) {
                                    endDates.push(endDateUTC);
                                }
                            }
                        }
                    }
                }
                
                if (endDates.length === 0) {
                    return '<em>Your subscription has no end date</em>';
                }
                
                // Find the maximum end date (compare timestamps)
                const maxEndDate = new Date(Math.max(...endDates.map(d => d.getTime())));
                
                // Renewal date is one day after the line item end date (in UTC)
                const renewalDate = new Date(Date.UTC(
                    maxEndDate.getUTCFullYear(),
                    maxEndDate.getUTCMonth(),
                    maxEndDate.getUTCDate() + 1
                ));

                const formatted = renewalDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    timeZone: 'UTC'
                });

                // Find subscription with a line item that has this end date
                const subWithDate = this.entity.subscriptions.find(sub => {
                    if (!sub) return false;
                    const lineItems = sub.line_items || sub.lineItems || [];
                    return lineItems.some(li => {
                        if (!li || typeof li !== 'object') return false;
                        const itemName = (li.name != null) ? String(li.name) : '';
                        const endDate = li.end_date;
                        if ((itemName === 'Enterprise Subscription' || itemName.includes('Avid')) && endDate) {
                            const liEndDateUTC = this.parseDateUTC(endDate);
                            return liEndDateUTC && liEndDateUTC.getTime() === maxEndDate.getTime();
                        }
                        return false;
                    });
                });
                
                if (subWithDate && subWithDate.proposal && subWithDate.proposal.executed_pdf_path) {
                    return `<a href="/api/billing/subscriptions/${subWithDate.id}/executed-pdf" target="_blank">${formatted}</a>`;
                }

                return formatted;
            },
            agreementName() {
                if (!this.entity?.subscriptions || this.entity.subscriptions.length === 0) {
                    return 'Avid';
                }
                for (const subscription of this.entity.subscriptions) {
                    if (!subscription || typeof subscription !== 'object') continue;
                    const lineItems = subscription.line_items || subscription.lineItems || [];
                    const hasEnterprise = lineItems.some(
                        item => item && typeof item === 'object' && item.name === 'Enterprise Subscription'
                    );
                    if (hasEnterprise) {
                        return 'Enterprise Subscription';
                    }
                }
                return 'Avid';
            },
            currentYear() {
                return new Date().getFullYear();
            },
            currentYearInvoices() {
                if (!this.past_invoices || this.past_invoices.length === 0) return [];
                const currentYear = this.currentYear;
                return this.past_invoices.filter(inv => {
                    if (!inv.date) return false;
                    const invDate = this.parseDateUTC(inv.date);
                    if (!invDate) return false;
                    return invDate.getUTCFullYear() === currentYear;
                });
            },
            previousYearsInvoices() {
                if (!this.past_invoices || this.past_invoices.length === 0) return [];
                const currentYear = this.currentYear;
                const yearMap = new Map();
                
                this.past_invoices.forEach(inv => {
                    if (!inv.date) return;
                    const invDate = this.parseDateUTC(inv.date);
                    if (!invDate) return;
                    const year = invDate.getUTCFullYear();
                    
                    // Only include years strictly less than current year
                    if (year < currentYear) {
                        if (!yearMap.has(year)) {
                            yearMap.set(year, []);
                        }
                        yearMap.get(year).push(inv);
                    }
                });
                
                // Convert to array, sort invoices by date descending, and sort by year descending
                return Array.from(yearMap.entries())
                    .map(([year, invoices]) => {
                        // Sort invoices by date descending
                        const sortedInvoices = invoices.sort((a, b) => {
                            const dateA = new Date(a.date);
                            const dateB = new Date(b.date);
                            return dateB - dateA;
                        });
                        return { year, invoices: sortedInvoices };
                    })
                    .sort((a, b) => b.year - a.year);
            },
            cardImage(){
                if(!this.payment_method) return null;
                if(this.payment_method.type==='bank') {
                    return '/img/images/bank_account_card.png';
                }

                const brand = this.payment_method.brand?.toLowerCase();
                switch(brand){
                    case 'visa': return '/img/images/visa_credit_card.png';
                    case 'mastercard': return '/img/images/mastercard_credit_card.png';
                    case 'american express': return '/img/images/amex_credit_card.png';
                    case 'amex': return '/img/images/amex_credit_card.png';
                    case 'discover': return '/img/images/discover_credit_card.png';
                    default: return null; // default fallback
                }
            },
            cardStyle(){
                if(!this.cardImage) return {};
                return {
                    backgroundImage: `url(${this.cardImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                };
            },
            maskedNumber(){
                if(!this.payment_method) return '';
                let bulletCount = 8; // default bank
                if(this.payment_method.type==='card'){
                    const brand = this.payment_method.brand?.toLowerCase();
                    bulletCount = (brand==='american express' || brand==='amex') ? 11 : 12;
                }
                const bullets = '•'.repeat(bulletCount);
                // group in chunks of 4 for readability
                return bullets.replace(/(.{4})/g,'$1 ').trim();
            },
            lockstepSupportLink() {
                const candidates = [
                    this.entity && this.entity.url,
                    this.user && this.user.client && this.user.client.url,
                ];

                const url = candidates.find(value => value && typeof value === 'string' && value.trim() !== '');

                return url ? `/lockstep/${url}` : 'mailto:help@avidai.com';
            }
        },
        methods: {
            formatFullDate(dateValue) {
                if (!dateValue) return '';
                const date = new Date(dateValue);
                const offset = date.getTimezoneOffset();
                date.setMinutes(date.getMinutes() + offset);
                
                const monthNames = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];
                const month = monthNames[date.getMonth()];
                const day = date.getDate();
                const year = date.getFullYear();
                
                return `${month} ${day}, ${year}`;
            },
            parseDateUTC(dateStr) {
                if (!dateStr) return null;
                if (typeof dateStr === 'string') {
                    const parts = dateStr.split('T')[0].split('-');
                    if (parts.length === 3) {
                        const y = parseInt(parts[0]);
                        const m = parseInt(parts[1]) - 1;
                        const d = parseInt(parts[2]);
                        return new Date(Date.UTC(y, m, d));
                    }
                }
                // fallback
                return new Date(dateStr);
            },
            getPaymentIcon(payment) {
                // Use entity payment_method for ACH
                if (this.entity.payment_method === 'ach') {
                    return '/img/icons/avid-icons/bank.svg';
                }
                
                // Bill.com case - return special value for text display
                if (this.entity.payment_method === 'bill.com') {
                    return '/img/images/bill_com_logo.svg';
                }
                
                // Custom payment method case - return special value for text display
                if (this.entity.payment_method && this.entity.payment_method !== 'stripe' && this.entity.payment_method !== 'ach' && this.entity.payment_method !== 'bill.com') {
                    return 'custom';
                }
                
                // Use payment_method object if available
                if (this.payment_method) {
                    if (this.payment_method.type === 'bank') {
                        return '/img/icons/avid-icons/bank.svg';
                    }
                    
                    if (this.payment_method.type === 'card' && this.payment_method.brand) {
                        const brand = this.payment_method.brand.toLowerCase();
                        switch(brand) {
                            case 'visa': return '/img/images/visa_logo.svg';
                            case 'mastercard': return '/img/images/mastercard_logo.svg';
                            case 'american express': return '/img/images/amex_logo.svg';
                            case 'amex': return '/img/images/amex_logo.svg';
                            case 'discover': return '/img/images/discover_logo.svg';
                            default: return '/img/images/visa_logo.svg'; // default fallback
                        }
                    }
                }
                
                // Default to card icon if payment_method is stripe
                if (this.entity.payment_method === 'stripe') {
                    return '/img/images/stripe_logo.svg';
                }
                
                // Fallback
                return '/img/icons/avid-icons/bank.svg';
            },
            setBillingContact(contact) {
                this.billing_contact_form = {
                    first_name: contact.first_name || '',
                    last_name: contact.last_name || '',
                    email: contact.email || '',
                    phone: contact.phone || '',
                    address: contact.address || '',
                    city: contact.city || '',
                    region: contact.region || '',
                    postal_code: contact.postal_code || '',
                    country: contact.country || '' 
                };
            },
            async fetchPaymentMethod() {
                this.payment_loading = true;
                try {
                    const { data } = await axios.get(`/api/billing/payment-method/${this.entityType}/${this.entity.id}`);
                    this.payment_method = data.data;
                } catch (e) {
                    console.error('Failed to load payment method', e);
                } finally {
                    this.payment_loading = false;
                }
            },
            async startUpdatePaymentMethod() {
                try {
                    // 1) Ask your API to create a SetupIntent and return its client_secret
                    const { data } = await axios.post(
                        `/api/billing/payment-method/${this.entityType}/${this.entity.id}/intent`
                    );
                    const clientSecret = data.client_secret;

                    // 2) Init Stripe + Elements
                    const stripe = await this.getStripe();
                    const elements = stripe.elements({
                        clientSecret,
                        appearance: {
                            theme: 'stripe',
                            variables: {
                                borderRadius: '20px'
                            },
                            rules: {
                                '.AccordionItem, .p-AccordionItem': {
                                    borderRadius: '20px'
                                }
                            }
                        }
                    });
                    const paymentElement = elements.create('payment');

                    // 3) Show modal with the Payment Element and confirm in preConfirm
                    const { isConfirmed, value } = await Swal.fire({
                        title: 'Update Payment Method',
                        html: '<div id="payment-element" style="margin-top:8px;"></div>',
                        showCancelButton: true,
                        focusConfirm: false,
                        confirmButtonText: 'Save Payment Method',
                        cancelButtonText: 'Cancel',
                        customClass: { popup: 'swal-info' },
                        didOpen: () => {
                            paymentElement.mount('#payment-element');
                        },
                        willClose: () => {
                            try { paymentElement.unmount(); } catch {}
                            try { elements.destroy(); } catch {}
                        },
                        preConfirm: async () => {
                            // Elements submit first (handles real-time validation)
                            const { error: submitError } = await elements.submit();
                            if (submitError) {
                            Swal.showValidationMessage(submitError.message);
                            return false;
                            }

                            // Confirm the SetupIntent (no redirect unless required)
                            const { setupIntent, error } = await stripe.confirmSetup({
                            elements,
                            clientSecret,
                            redirect: 'if_required', // avoids page navigation for most cards
                            });

                            if (error) {
                            Swal.showValidationMessage(error.message);
                            return false;
                            }

                            // Return to Swal as the modal "value"
                            return setupIntent;
                        }
                    });

                    if (!isConfirmed) return;

                    const setupIntent = value; // <- from preConfirm return
                    // 4) Tell your server which PaymentMethod to attach & set as default
                    await axios.post(
                        `/api/billing/payment-method/${this.entityType}/${this.entity.id}`,
                        { payment_method_id: setupIntent.payment_method, payment_method: 'stripe' }
                    );

                    // 5) Refresh UI and notify
                    await this.fetchPaymentMethod();
                    this.showNotification({
                        text: 'Your payment method has been updated!'
                    });
                } catch (e) {
                    console.error(e);
                    Swal.fire('Error', e?.response?.data?.message || e.message || 'Something went wrong', 'error');
                }
                },
            async getStripe() {
                if (!this.$root.$stripe) {
                    const stripeKey = document.head.querySelector('meta[name="stripe-key"]').content;
                    this.$root.$stripe = await loadStripe(stripeKey);
                }
                return this.$root.$stripe;
            },
            sortByKey(key) {
                this.sort_key = key;
                this.sort_order = (this.sort_order === 'asc') ? 'des' : 'asc';
                this.sortPastInvoices();
            },
            sortPastInvoices() {
                if (!this.filtered_past_invoices || !this.sort_key) return;
                this.filtered_past_invoices.sort((a, b) => {
                    let valA = a[this.sort_key];
                    let valB = b[this.sort_key];
                    // For amount, sort numerically
                    if (this.sort_key === 'net_amount') {
                        valA = Number(valA);
                        valB = Number(valB);
                    }
                    // For date, sort as date
                    if (this.sort_key === 'date') {
                        valA = new Date(valA);
                        valB = new Date(valB);
                    }
                    // For paid_by, sort by payer name
                    if (this.sort_key === 'paid_by') {
                        valA = (a.paid_by?.name || '').toLowerCase();
                        valB = (b.paid_by?.name || '').toLowerCase();
                    }
                    if (valA < valB) return this.sort_order === 'asc' ? -1 : 1;
                    if (valA > valB) return this.sort_order === 'asc' ? 1 : -1;
                    return 0;
                });
                this.reloadPagination(this.filtered_past_invoices);
            },
            getInvoiceBadgeClass(status) {
                switch (status) {
                    case 'paid': return 'bg-green bg-icon-success';
                    case 'uncollectible': 
                    case 'open': return 'bg-red bg-icon-danger';
                    case 'draft': return 'bg-yellow bg-icon-warning';
                    default: return 'badge-secondary';
                }
            },
            getInvoiceStatusLabel(status) {
                switch (status) {
                    case 'draft': return 'Due Soon';
                    case 'open': return 'Overdue';
                    case 'paid': return 'Paid';
                    case 'void': return 'Void';
                    case 'uncollectible': return 'Uncollectible';
                    default: return 'Unknown';
                }
            },
            statusClass(status){
                 switch(status){
                    case 'ok': return 'bg-green bg-icon-success';
                    case 'failed': return 'bg-red bg-icon-danger';
                    case 'expired': return 'bg-red bg-icon-danger';
                    case 'missing': return 'bg-red bg-icon-danger';

                 }
             },
            async startEditBillingContact() {
                

                const { isConfirmed, value } = await Swal.fire({
                    title: this.entity.billing_contact ? 'Edit Billing Contact' : 'Add Billing Contact',
                    html: this.getBillingContactFormHTML(),
                    showCancelButton: true,
                    focusConfirm: false,
                    confirmButtonText: 'Save',
                    cancelButtonText: 'Cancel',
                    customClass: { popup: 'swal-info' },
                    didOpen: () => {
                        this.populateFormFields();

                        // Populate country dropdown
                        const countrySelect = document.getElementById('country');
                        if (countrySelect && countrySelect.options.length === 0) {
                            allCountries.forEach(c => {
                                const opt = document.createElement('option');
                                opt.value = c[1]; // short code
                                opt.text  = c[0]; // full name
                                countrySelect.appendChild(opt);
                            });
                        }

                        // Set current value (default to US)
                        countrySelect.value = this.billing_contact_form.country || 'US';
                    },
                    preConfirm: () => {
                        return this.validateBillingContactForm();
                    }
                });

                if (!isConfirmed) return;

                await this.saveBillingContact();
            },
            getBillingContactFormHTML() {
                return `
                    <div class="billing-contact-form main">
                        <div class="row mb-3">
                            <div class="col-md-6 text-start">
                                <label for="first_name" class="form-label">First Name *</label>
                                <input type="text" class="form-control" id="first_name" name="first_name" required aria-label="First Name *">
                            </div>
                            <div class="col-md-6 text-start">
                                <label for="last_name" class="form-label">Last Name *</label>
                                <input type="text" class="form-control" id="last_name" name="last_name" required aria-label="Last Name *">
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col-md-6 text-start">
                                <label for="email" class="form-label">Email *</label>
                                <input type="email" class="form-control" id="email" name="email" required aria-label="Email *">
                            </div>
                            <div class="col-md-6 text-start">
                                <label for="phone" class="form-label">Phone</label>
                                <input type="tel" class="form-control" id="phone" name="phone" aria-label="Phone">
                            </div>
                        </div>
                        <div class="mb-3 text-start">
                            <label for="address" class="form-label">Address *</label>
                            <input class="form-control" id="address" name="address" aria-label="Address *">
                        </div>
                        <div class="row mb-3">
                            <div class="col-md-4  text-start">
                                <label for="city" class="form-label">City *</label>
                                <input type="text" class="form-control" id="city" name="city" aria-label="City *">
                            </div>
                            <div class="col-md-4  text-start">
                                <label for="region" class="form-label">State/Region *</label>
                                <input type="text" class="form-control" id="region" name="region" aria-label="State/Region *">
                            </div>
                            <div class="col-md-4 text-start">
                                <label for="postal_code" class="form-label">Postal Code *</label>
                                <input type="text" class="form-control" id="postal_code" name="postal_code" aria-label="Postal Code *">
                            </div>
                        </div>
                        <div class="col text-start"> 
                            <label for="country" class="form-label">Country *</label>
                            <select class="form-select" id="country" name="country" aria-label="Country *"></select>
                        </div>
                    </div>
                `;
            },
            populateFormFields() {
                // Populate form fields with current data
                Object.keys(this.billing_contact_form).forEach(key => {
                    const field = document.getElementById(key);
                    if (field) {
                        field.value = this.billing_contact_form[key];
                    }
                });
            },
            validateBillingContactForm() {
                // Get form values directly from input elements
                const formValues = {};
                
                // Get all input, select & textarea fields from the form
                const fields = document.querySelectorAll('.billing-contact-form input, .billing-contact-form textarea, .billing-contact-form select');
                fields.forEach(field => {
                    if (field.name) {
                        formValues[field.name] = field.value;
                    }
                });

                // Validate required fields
                if (!formValues.first_name?.trim()) {
                    Swal.showValidationMessage('First name is required');
                    return false;
                }
                if (!formValues.last_name?.trim()) {
                    Swal.showValidationMessage('Last name is required');
                    return false;
                }
                if (!formValues.email?.trim()) {
                    Swal.showValidationMessage('Email is required');
                    return false;
                }
                if (formValues.email && !this.isValidEmail(formValues.email)) {
                    Swal.showValidationMessage('Please enter a valid email address');
                    return false;
                }
                if (!formValues.address?.trim()) {
                    Swal.showValidationMessage('Address is required');
                    return false;
                }
                if (!formValues.city?.trim()) {
                    Swal.showValidationMessage('City is required');
                    return false;
                }  
                if (!formValues.region?.trim()) {
                    Swal.showValidationMessage('State/Region is required');
                    return false;
                }
                if (!formValues.postal_code?.trim()) {
                    Swal.showValidationMessage('Postal Code is required');
                    return false;
                }

                // Validate that region belongs to selected country
                if (!this.isValidRegion(formValues.country, formValues.region)) {
                    Swal.showValidationMessage('Region / State is not valid for the selected country');
                    return false;
                }

                // Update form data
                this.billing_contact_form = formValues;
                return true;
            },
            isValidEmail(email) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailRegex.test(email);
            },
            // Validate region/state based on selected country using allCountries dataset
            isValidRegion(countryCode, regionInput) {
                if (!countryCode || !regionInput) return false;
                const country = allCountries.find(c => (c[1] || '').toLowerCase() === countryCode.toLowerCase());
                if (!country) return false;
                const regionsArr = country[2] || [];
                const input = regionInput.trim().toLowerCase();
                return regionsArr.some(r => {
                    const rName = (r[0] || '').toLowerCase();
                    const rCode = (r[1] || '').toLowerCase();
                    return input === rName || input === rCode;
                });
            },
            async saveBillingContact() {
                
                try {
                    const url = `/api/billing/contact/${this.entityType}/${this.entity.id}`;
                    const method = this.entity.billing_contact ? 'PUT' : 'POST';
                    
                    const { data } = await axios({
                        method: method,
                        url: url,
                        data: this.billing_contact_form
                    });

                    // Update the entity's billing contact
                    this.setBillingContact(data.data);
                    

                    this.showNotification({
                        text: 'Your billing contact has been ' + (this.entity.billing_contact ? 'updated' : 'saved') + '!'
                    });
                } catch (e) {
                    //Show the notification
                    this.showNotification({
                        text: 'Failed to save your billing contact. Please try again.',
                        type: 'error'
                    });
                    console.error('Failed to save billing contact', e);
                }
            },
            // ================================
            // Invoice Modal Helpers
            // ================================
            
            showInvoice(invoice) {
                if (!invoice) return;
                this.selectedInvoice = invoice;
                
                this.$nextTick(() => {
                    const modalElement = document.getElementById('invoice-details-modal');
                    if (modalElement) {
                        const existingModal = window.bootstrap?.Modal?.getInstance(modalElement);
                        if (existingModal) {
                            existingModal.dispose();
                        }
                        
                        const modal = new window.bootstrap.Modal(modalElement, {
                            backdrop: 'static',
                            keyboard: true,
                            focus: true
                        });
                        
                        const handleHidden = () => {
                            this.selectedInvoice = null;
                            modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                        };
                        modalElement.addEventListener('hidden.bs.modal', handleHidden);
                        
                        modal.show();
                        
                        // Initialize tooltips
                        this.$nextTick(() => {
                            const tooltipTriggerList = modalElement.querySelectorAll('[data-bs-toggle="tooltip"]');
                            [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                                const existingTooltip = window.bootstrap?.Tooltip?.getInstance(tooltipTriggerEl);
                                if (existingTooltip) {
                                    existingTooltip.dispose();
                                }
                                new window.bootstrap.Tooltip(tooltipTriggerEl);
                            });
                        });
                    }
                });
            },
            closeInvoiceModal() {
                const modalElement = document.getElementById('invoice-details-modal');
                if (modalElement) {
                    const modal = window.bootstrap?.Modal?.getInstance(modalElement);
                    if (modal) {
                        modal.hide();
                    }
                }
                this.selectedInvoice = null;
            },
            getInvoiceTitle() {
                if (!this.selectedInvoice) return '';
                // You can customize this based on your needs
                return this.entityType === 'agency' && this.selectedInvoice.client 
                    ? this.selectedInvoice.client.name 
                    : 'Avid';
            },
            formatInvoiceDate(dateString) {
                if (!dateString) return '';
                const date = this.parseDateUTC(dateString);
                if (!date) return '';
                const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
                return date.toLocaleDateString('en-US', options);
            },
            getInvoiceLineItems() {
                if (!this.selectedInvoice) return [];
                const lineItems = this.selectedInvoice.line_items || this.selectedInvoice.lineItems || [];
                
                if (lineItems.length === 0) {
                    return [{
                        name: 'No items',
                        description: '',
                        amount: 0,
                        taxAmount: 0
                    }];
                }
                
                return lineItems.map(item => {
                    // net_amount is the amount after discount but before tax
                    // total_amount is the original amount before discount
                    const totalAmount = Number(item.total_amount ?? 0);
                    const netAmount = Number(item.net_amount ?? totalAmount);
                    const taxAmount = Number(item.tax_amount ?? 0);
                    const hasDiscount = netAmount < totalAmount && totalAmount > 0;
                    const discountPercent = hasDiscount ? Math.round((1 - netAmount / totalAmount) * 100) : 0;
                    
                    return {
                        name: item.name || 'Unnamed Item',
                        description: item.description || '',
                        quantity: item.quantity ?? 1,
                        amount: netAmount,
                        originalAmount: totalAmount,
                        hasDiscount: hasDiscount,
                        discountPercent: discountPercent,
                        taxAmount: taxAmount
                    };
                });
            },
            getInvoiceMonthTotal() {
                if (!this.selectedInvoice || !this.selectedInvoice.date) return '';
                const date = new Date(this.selectedInvoice.date);
                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'];
                return monthNames[date.getUTCMonth()];
            },
            downloadInvoice(inv){
                if(!inv || !inv.id) return;
                window.open(`/api/billing/invoice/${inv.id}/download`, '_blank');
            }
        }
    })
</script>
